import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { Box, Chip, IconButton, LinearProgress, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CustomLoader from 'components/shared/CustomLoader/CustomLoader';
import Pagination from 'components/shared/CustomPagination/CustomPagination';
import InfiniteScrollColumn from 'components/shared/InfiniteScrollColumn/InfiniteScrollColumn';
import { SortPopover } from 'components/shared/SortPopOver';
import formatCurrency, { formatDealAmount } from 'helper/short-currency';
import {
  ClientType,
  EnterpriseClientGroup,
  GroupedClientListResponse,
  GroupedClientNode,
  InfiniteScrollData,
  PaginationProps,
  StandaloneGroupedClient,
} from 'model/client.model';
import { LayoutType } from 'model/deals.model';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import zeplinColor from 'theme/Color';
import { ClientHealthStatus, getHealthBarColor, getStatusChipColor } from './clientHealth';
import CustomClientCard from './CustomClientCard';
import { getCategoryChipColor } from './utilities/clientCategory';

const formatClientTypeLabel = (value?: string) => {
  if (!value) return 'NA';

  return value
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

const toCardClient = (client: GroupedClientNode | StandaloneGroupedClient): ClientType => ({
  Name: client.name || 'N/A',
  Id: client.clientId || client.name,
  AccountId: client.clientId || client.name,
  closedDealAmount: null,
  openDealAmount: null,
  category: formatClientTypeLabel(client.clientType),
  avatar: client.profileImageUrl || null,
  healthScore: client.healthScore ?? null,
  status: client.healthStatus || null,
  primaryMetricLabel: 'TTM Revenue',
  primaryMetricValue: client.ttmRevenue ?? null,
  secondaryMetricLabel: 'Deals',
  secondaryMetricValue: client.activeDealCount ?? null,
  tertiaryMetricLabel: 'Owner',
  tertiaryMetricValue: client.ownerName ?? 'N/A',
});

const ClientCardLink = ({ client }: { client: GroupedClientNode | StandaloneGroupedClient }) => {
  const card = <CustomClientCard client={toCardClient(client)} loading={false} />;

  if (!client.clientId) {
    return <Box>{card}</Box>;
  }

  return (
    <Link
      to={`huddle/${client.clientId}`}
      state={{ client: toCardClient(client) }}
      style={{ textDecoration: 'none', color: zeplinColor.BasicBlack }}
    >
      {card}
    </Link>
  );
};

const getInitials = (name?: string) => {
  const val = (name || 'N/A').trim();
  const parts = val.split(' ').filter(Boolean);
  if (!parts.length) return 'NA';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
};

const getCategoryLabel = (clientType?: string) => formatClientTypeLabel(clientType);

const getStatusLabel = (healthStatus?: string, healthScore?: number): ClientHealthStatus => {
  const normalized = (healthStatus || '').trim().toLowerCase();

  if (normalized === 'strong') return 'Strong';
  if (normalized === 'good') return 'Good';
  if (normalized === 'need attention' || normalized === 'need_attention') return 'Need Attention';

  const score = Number.isFinite(Number(healthScore)) ? Number(healthScore) : 0;
  if (score >= 70) return 'Strong';
  if (score >= 10) return 'Good';
  return 'Need Attention';
};

type GroupedTableRow = {
  key: string;
  clientId?: string;
  name: string;
  clientType?: string;
  league?: string;
  ownerName?: string;
  deals?: number;
  ttmRevenue?: number;
  healthScore?: number;
  healthStatus?: string;
  profileImageUrl?: string;
  isParent?: boolean;
};

type GroupTableColumn = {
  id: string;
  label: string;
  align?: 'center';
  sortable?: boolean;
  apiField?: string;
  numeric?: boolean;
};

const groupTableColumns: GroupTableColumn[] = [
  { id: 'name', label: 'Name', sortable: true, apiField: 'name' },
  { id: 'category', label: 'Type', sortable: false },
  { id: 'league', label: 'League', sortable: false },
  { id: 'status', label: 'Status', align: 'center', sortable: true, apiField: 'healthStatus' },
  { id: 'health', label: 'Health', sortable: true, apiField: 'healthScore', numeric: true },
  {
    id: 'ttmRevenue',
    label: 'TTM Revenue',
    align: 'center',
    sortable: true,
    apiField: 'ttmRevenue',
    numeric: true,
  },
  {
    id: 'deals',
    label: 'Deals',
    align: 'center',
    sortable: true,
    apiField: 'activeDealCount',
    numeric: true,
  },
  { id: 'ownerName', label: 'Owner', align: 'center', sortable: true, apiField: 'ownerName' },
];

const GroupTable = ({
  rows,
  onSort,
}: {
  rows: GroupedTableRow[];
  onSort?: (field: string, order: 'asc' | 'desc') => void;
}) => {
  const navigate = useNavigate();
  const [sortAnchorEl, setSortAnchorEl] = useState<{ el: HTMLElement; id: string } | null>(null);
  const [sortOptions, setSortOptions] = useState<Record<string, 'asc' | 'desc'>>({});

  const handleSortClick = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setSortAnchorEl({ el: event.currentTarget, id });
  };

  const handleSortChange = (order: 'asc' | 'desc') => {
    if (!sortAnchorEl) return;
    const col = groupTableColumns.find((c) => c.id === sortAnchorEl.id);
    const apiField = col?.apiField || sortAnchorEl.id;
    setSortOptions((prev) => ({ ...prev, [sortAnchorEl.id]: order }));
    if (onSort) onSort(apiField, order);
    setSortAnchorEl(null);
  };

  const activeSortCol = groupTableColumns.find((c) => c.id === sortAnchorEl?.id);

  return (
    <>
      <Paper sx={{ width: '100%', border: 'none', boxShadow: 'none' }}>
        <TableContainer
          sx={{
            overflow: 'auto',
            border: `1px solid ${zeplinColor.Neutral5}`,
            borderRadius: '14px',
          }}
        >
          <Table stickyHeader aria-label='grouped clients table'>
            <TableHead>
              <TableRow
                sx={{
                  '& th': {
                    backgroundColor: zeplinColor.Neutral3,
                    color: zeplinColor.BasicBlack,
                  },
                  '& th:first-child': {
                    borderRadius: '14px 0px 0px 14px',
                  },
                  '& th:last-child': {
                    borderRadius: '0px 14px 14px 0px',
                  },
                }}
              >
                {groupTableColumns.map((col) => (
                  <TableCell key={col.id} align={col.align}>
                    {col.sortable ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          justifyContent: col.align === 'center' ? 'center' : 'flex-start',
                        }}
                      >
                        <Typography
                          variant='responsiveBody3'
                          sx={{ color: zeplinColor.BasicBlack }}
                        >
                          {col.label}
                        </Typography>
                        <IconButton
                          size='small'
                          onClick={(e) => handleSortClick(e, col.id)}
                          sx={{ padding: '2px' }}
                        >
                          <UnfoldMoreIcon
                            sx={{ fontSize: '16px', color: zeplinColor.BasicBlack }}
                          />
                        </IconButton>
                      </Box>
                    ) : (
                      <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
                        {col.label}
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const score = Math.max(0, Math.min(100, Number(row.healthScore || 0)));
                const statusLabel = getStatusLabel(row.healthStatus, score);
                const categoryLabel = getCategoryLabel(row.clientType);

                return (
                  <TableRow
                    key={row.key}
                    hover
                    onClick={() => {
                      if (!row.clientId) return;
                      navigate(`huddle/${row.clientId}`, {
                        state: { client: toCardClient(row as any) },
                      });
                    }}
                    sx={{
                      cursor: row.clientId ? 'pointer' : 'default',
                    }}
                  >
                    {/* Name */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {row.profileImageUrl ? (
                          <img
                            width={'44px'}
                            height={'44px'}
                            src={row.profileImageUrl}
                            alt='logo'
                            style={{ objectFit: 'cover', borderRadius: '10px' }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '44px',
                              height: '44px',
                              borderRadius: '10px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: zeplinColor.Brand1,
                            }}
                          >
                            <Typography variant='body3' sx={{ color: zeplinColor.Neutral1 }}>
                              {getInitials(row.name)}
                            </Typography>
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Typography variant='body3' color={zeplinColor.BasicBlack}>
                            {row.name || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    {/* Type */}
                    <TableCell>
                      <Chip
                        label={categoryLabel}
                        sx={{
                          backgroundColor: getCategoryChipColor(categoryLabel),
                          color: zeplinColor.Neutral1,
                          borderRadius: '50px',
                          height: '24px',
                          '& .MuiChip-label': {
                            px: '10px',
                          },
                        }}
                      />
                    </TableCell>
                    {/* League */}
                    <TableCell>
                      <Typography variant='body3' color={zeplinColor.BasicBlack}>
                        {row.league || '—'}
                      </Typography>
                    </TableCell>
                    {/* Status */}
                    <TableCell align='center'>
                      <Chip
                        label={statusLabel}
                        sx={{
                          backgroundColor: getStatusChipColor(statusLabel),
                          color: zeplinColor.Neutral1,
                          borderRadius: '50px',
                          height: '24px',
                          '& .MuiChip-label': {
                            px: '10px',
                          },
                        }}
                      />
                    </TableCell>
                    {/* Health */}
                    <TableCell>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 160 }}
                      >
                        <LinearProgress
                          variant='determinate'
                          value={score}
                          sx={{
                            flex: 1,
                            height: 8,
                            borderRadius: '999px',
                            backgroundColor: zeplinColor.Neutral4,
                            '& .MuiLinearProgress-bar': {
                              borderRadius: '999px',
                              backgroundColor: getHealthBarColor(score),
                            },
                          }}
                        />
                        <Typography variant='label1' color={getHealthBarColor(score)}>
                          {score}%
                        </Typography>
                      </Box>
                    </TableCell>
                    {/* TTM Revenue */}
                    <TableCell align='center'>
                      <Typography variant='body3' color={zeplinColor.BasicBlack}>
                        {formatDealAmount(row.ttmRevenue)}
                      </Typography>
                    </TableCell>
                    {/* Deals */}
                    <TableCell align='center'>
                      <Typography variant='body3' color={zeplinColor.Brand1}>
                        {row.deals ?? 0}
                      </Typography>
                    </TableCell>
                    {/* Owner */}
                    <TableCell align='center'>
                      <Typography variant='body3' color={zeplinColor.BasicBlack}>
                        {row.ownerName || 'N/A'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <SortPopover
        anchorEl={sortAnchorEl?.el || null}
        setAnchorEl={() => setSortAnchorEl(null)}
        sortOption={sortAnchorEl ? sortOptions[sortAnchorEl.id] || 'asc' : 'asc'}
        onSortChange={handleSortChange}
        label={activeSortCol?.label || ''}
        ascLabel={activeSortCol?.numeric ? 'Lowest to Highest' : 'A to Z'}
        descLabel={activeSortCol?.numeric ? 'Highest to Lowest' : 'Z to A'}
      />
    </>
  );
};

const PaginationBar = ({
  pagination,
  dataLength,
}: {
  pagination: PaginationProps;
  dataLength: number;
}) => {
  const totalPages = pagination.totalPages ?? 0;
  const handleFirstPage = () => {
    pagination.setCurrentPage(0);
    pagination.setPageWindowStart(0);
  };
  const handlePreviousPage = () => {
    if (pagination.currentPage > 0) pagination.setCurrentPage(pagination.currentPage - 1);
  };
  const handleNextPage = () => {
    if (pagination.currentPage < totalPages - 1)
      pagination.setCurrentPage(pagination.currentPage + 1);
  };
  const handleLastPage = () => {
    pagination.setCurrentPage(totalPages - 1);
    pagination.setPageWindowStart(Math.max(0, totalPages - 4));
  };

  return (
    <Pagination
      currentPage={pagination.currentPage}
      totalPages={totalPages}
      rowsPerPage={pagination.rowsPerPage}
      count={pagination.totalCount}
      paginatedDataLength={dataLength}
      onFirstPage={handleFirstPage}
      onPreviousPage={handlePreviousPage}
      onNextPage={handleNextPage}
      onLastPage={handleLastPage}
      onPageChange={(page) => pagination.setCurrentPage(page)}
      onRowsPerPageChange={(val) => {
        pagination.setRowsPerPage(val);
        pagination.setCurrentPage(0);
      }}
      pageWindowStart={pagination.pageWindowStart}
      setPageWindowStart={pagination.setPageWindowStart}
      setCurrentPage={pagination.setCurrentPage}
      setRowsPerPage={pagination.setRowsPerPage}
    />
  );
};

const EnterpriseSection = ({ enterprise }: { enterprise: EnterpriseClientGroup }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
          {enterprise.enterpriseName || 'N/A'}
        </Typography>

        <Box
          sx={{
            minWidth: '160px',
            display: 'flex',
            gap: '4px',
            alignItems: 'center',
          }}
        >
          <Typography variant='label1' color={zeplinColor.Neutral8}>
            Combined Rev
          </Typography>
          <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
            ${formatCurrency(Number(enterprise.combinedRevenue || 0))}
          </Typography>
        </Box>

        <Box
          sx={{
            minWidth: '120px',
            display: 'flex',
            gap: '4px',
            alignItems: 'center',
          }}
        >
          <Typography variant='label1' color={zeplinColor.Neutral8}>
            Entities
          </Typography>
          <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
            {enterprise.entityCount ?? enterprise.children?.length ?? 0}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '340px' }}>
          <ClientCardLink client={enterprise.parent} />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(auto-fill, minmax(250px, 1fr))',
                sm: 'repeat(auto-fill, minmax(270px, 1fr))',
                md: 'repeat(auto-fill, minmax(280px, 1fr))',
                lg: 'repeat(auto-fill, minmax(290px, 1fr))',
                xl: 'repeat(auto-fill, minmax(300px, 1fr))',
              },
              gap: '24px',
              gridAutoRows: 'min-content',
              gridAutoFlow: 'row',
              '& > a': {
                height: 'auto',
                display: 'block',
              },
            }}
          >
            {(enterprise.children || []).map((child, index) => (
              <ClientCardLink
                key={child.clientId || `${enterprise.enterpriseName}-child-${index}`}
                client={child}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const GroupedClientsList = ({
  groupedData,
  loading,
  layout,
  pagination,
  infiniteScroll,
  onSort,
}: {
  groupedData?: GroupedClientListResponse | null;
  loading: boolean;
  layout: LayoutType;
  pagination?: PaginationProps;
  infiniteScroll?: InfiniteScrollData;
  onSort?: (field: string, order: 'asc' | 'desc') => void;
}) => {
  if (loading)
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 190px)',
        }}
      >
        <CustomLoader open={true} />
      </Box>
    );

  const enterprises = groupedData?.enterprises || [];
  const standalone = groupedData?.standalone || [];
  const hasContent = enterprises.length > 0 || standalone.length > 0;

  if (!hasContent) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '320px',
        }}
      >
        <Typography variant='responsiveBody3' color={zeplinColor.Neutral8}>
          No grouped clients available.
        </Typography>
      </Box>
    );
  }

  if (layout === LayoutType.LIST) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          overflowY: 'auto',
          height: { xs: 'calc(100vh - 190px)', xl: 'calc(100vh - 250px)' },
          paddingRight: '4px',
        }}
      >
        {enterprises.map((enterprise, index) => {
          const rows: GroupedTableRow[] = [
            {
              key: `${enterprise.parentClientId || enterprise.enterpriseName}-parent`,
              clientId: enterprise.parent?.clientId,
              name: enterprise.parent?.name || 'N/A',
              clientType: enterprise.parent?.clientType,
              league: (enterprise.parent as any)?.league || null,
              ownerName: enterprise.parent?.ownerName,
              deals: enterprise.parent?.activeDealCount,
              ttmRevenue: enterprise.parent?.ttmRevenue,
              healthScore: enterprise.parent?.healthScore,
              healthStatus: enterprise.parent?.healthStatus,
              profileImageUrl: enterprise.parent?.profileImageUrl,
              isParent: true,
            },
            ...(enterprise.children || []).map((child, childIndex) => ({
              key: `${enterprise.parentClientId || enterprise.enterpriseName}-child-${childIndex}`,
              clientId: child.clientId,
              name: child.name || 'N/A',
              clientType: child.clientType,
              league: (child as any)?.league || null,
              ownerName: child.ownerName,
              deals: child.activeDealCount,
              ttmRevenue: child.ttmRevenue,
              healthScore: child.healthScore,
              healthStatus: child.healthStatus,
              profileImageUrl: child.profileImageUrl,
              isParent: false,
            })),
          ];

          return (
            <Box
              key={enterprise.parentClientId || `${enterprise.enterpriseName}-${index}`}
              sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
              <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
                  {enterprise.enterpriseName || 'N/A'}
                </Typography>

                <Box
                  sx={{
                    minWidth: '160px',
                    display: 'flex',
                    gap: '4px',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant='label1' color={zeplinColor.Neutral8}>
                    Combined Rev
                  </Typography>
                  <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
                    ${formatCurrency(Number(enterprise.combinedRevenue || 0))}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    minWidth: '120px',
                    display: 'flex',
                    gap: '4px',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant='label1' color={zeplinColor.Neutral8}>
                    Entities
                  </Typography>
                  <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
                    {enterprise.entityCount ?? enterprise.children?.length ?? 0}
                  </Typography>
                </Box>
              </Box>
              <GroupTable rows={rows} onSort={onSort} />
            </Box>
          );
        })}

        {standalone.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
              Standalone Clients
            </Typography>
            <GroupTable
              rows={standalone.map((client, index) => ({
                key: client.clientId || `${client.name}-${index}`,
                clientId: client.clientId,
                name: client.name || 'N/A',
                clientType: client.clientType,
                league: (client as any)?.league || null,
                ownerName: client.ownerName,
                deals: client.activeDealCount,
                ttmRevenue: client.ttmRevenue,
                healthScore: client.healthScore,
                healthStatus: client.healthStatus,
                profileImageUrl: client.profileImageUrl,
              }))}
              onSort={onSort}
            />
          </Box>
        )}
        {pagination && (
          <PaginationBar
            pagination={pagination}
            dataLength={enterprises.length + standalone.length}
          />
        )}
      </Box>
    );
  }

  return (
    <InfiniteScrollColumn
      dataVal={{
        records: [...enterprises, ...standalone],
        totalRecords: infiniteScroll?.totalCount ?? enterprises.length + standalone.length,
      }}
      next={async (offset) => {
        if (offset && infiniteScroll) await infiniteScroll.fetchMore();
      }}
      loading={infiniteScroll?.loading || loading}
      total={infiniteScroll?.totalCount ?? enterprises.length + standalone.length}
      height='calc(100vh - 190px)'
      render={() => null}
      paddingBottom='2rem'
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          paddingRight: '4px',
        }}
      >
        {enterprises.map((enterprise, index) => (
          <EnterpriseSection
            key={enterprise.parentClientId || `${enterprise.enterpriseName}-${index}`}
            enterprise={enterprise}
          />
        ))}

        {standalone.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
              Standalone Clients
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(auto-fill, minmax(250px, 1fr))',
                  sm: 'repeat(auto-fill, minmax(270px, 1fr))',
                  md: 'repeat(auto-fill, minmax(280px, 1fr))',
                  lg: 'repeat(auto-fill, minmax(290px, 1fr))',
                  xl: 'repeat(auto-fill, minmax(300px, 1fr))',
                },
                gap: '24px',
                gridAutoRows: 'min-content',
                gridAutoFlow: 'row',
                '& > a': { height: 'auto', display: 'block' },
              }}
            >
              {standalone.map((client, index) => (
                <ClientCardLink
                  key={client.clientId || `${client.name}-${index}`}
                  client={client}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </InfiniteScrollColumn>
  );
};

export default GroupedClientsList;
